import { CRM_KEYS, nowIso, readCrmJson, writeCrmJson } from './storage'
import { opportunityService } from './OpportunityService'
import type { ContactType, FollowUp, FollowUpResult, LostReasonCode } from './types'

/**
 * FollowUpService — contact history and response capture.
 */
export class FollowUpService {
  list(opportunityId?: string): FollowUp[] {
    const all = readCrmJson<FollowUp[]>(CRM_KEYS.followUps, 'followUps', [])
    if (!opportunityId) return all
    return all.filter((f) => f.opportunityId === opportunityId)
  }

  create(input: {
    opportunityId: string
    contactType?: ContactType
    notes?: string
    result?: FollowUpResult | null
    createdBy?: string
  }): FollowUp {
    const entry: FollowUp = {
      id: crypto.randomUUID(),
      opportunityId: input.opportunityId,
      date: nowIso(),
      contactType: input.contactType ?? 'whatsapp',
      notes: input.notes ?? '',
      result: input.result ?? null,
      createdBy: input.createdBy ?? 'admin@masterhub.com',
      createdAt: nowIso(),
    }
    writeCrmJson(CRM_KEYS.followUps, 'followUps', [entry, ...this.list()])
    opportunityService.recordContact(input.opportunityId, entry.date)
    return entry
  }

  registerResponse(
    opportunityId: string,
    result: FollowUpResult,
    options?: {
      notes?: string
      lostReasonCode?: LostReasonCode
      lostReasonNotes?: string
      createdBy?: string
    },
  ): { followUp: FollowUp; opportunityId: string } {
    const followUp = this.create({
      opportunityId,
      contactType: 'whatsapp',
      notes: options?.notes ?? '',
      result,
      createdBy: options?.createdBy,
    })

    if (result === 'APPROVED') {
      opportunityService.moveStage(opportunityId, 'APPROVED')
    } else if (result === 'STILL_THINKING') {
      opportunityService.moveStage(opportunityId, 'NEGOTIATION')
    } else if (result === 'BOUGHT_ELSEWHERE') {
      opportunityService.markLost(
        opportunityId,
        options?.lostReasonCode ?? 'OTHER',
        options?.lostReasonNotes ?? '',
      )
    } else if (result === 'CANCELLED') {
      opportunityService.moveStage(opportunityId, 'CANCELLED')
    } else if (result === 'NO_RESPONSE') {
      opportunityService.moveStage(opportunityId, 'AWAITING')
    }

    return { followUp, opportunityId }
  }
}

export const followUpService = new FollowUpService()
